import ApiProducts from "api/ApiProducts"
import Button from "components/EvtKit/button/Button"
import ModalComponent from "components/EvtKit/modal/ModalV2"
import BodyText from "components/Typography/BodyText"
import Heading from "components/Typography/Heading"
import { initialExternalProductInfo, initialProductFilesObj, initialProductInfo } from "constants/product"
import { memo, useCallback, useEffect, useState } from "react"
import { useMutation } from "react-query"
import { changeModalState, clearProduct } from "reducers/products/products-actions"
import { useAppDispatch, useAppSelector } from "reducers/store"
import styled from "styled-components"
import IsExternal from "./IsExternal"
import IsFederated from "./IsFederated"
import ProductForm from "./ProductForm"
import clsx from "clsx"
import IntegrationForm from "./IntegrationForm"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 28px 0px 24px;
`
const BodyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: left;
  justify-content: left;
  margin: 0px auto;
`
const SeparatorLine = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${(props) => props.theme.palette.mediumDarkGray};
  opacity: 0.3;
  margin-top: 12px;
  margin-bottom: 28px;
`

const HeaderContainer = styled.div`
  display: grid;
  justify-self: left;
  gap: 16px;
  margin: 20px 0px 20px 0px;
`

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  gap: 24px;
  margin-top: 12px;
`

const ErrorMsg = styled.p`
  color: ${(props) => props.theme.palette.error};
  margin-top: 18px;
`

const ButtonCustom = styled(Button)`
  width: 100%;
`

const apiProducts = new ApiProducts()

const ProductModal = () => {
  const appDispatch = useAppDispatch()

  const { modal, product } = useAppSelector((state) => state.product)
  const { name, prefixUrl, id } = product

  const isTypeCreate = modal.type === "create"
  const isTypeUpdate = modal.type === "update"

  const [error, setError] = useState("")
  const [emailError, setEmailError] = useState("")
  const [descriptionError, setDescriptionError] = useState("")
  const [productName, setProductName] = useState(isTypeUpdate ? name : "")
  const [isFederated, setIsFederated] = useState<boolean>(false)
  const [isExternal, setIsExternal] = useState<boolean>(false)
  const [federatedKey, setFederatedKey] = useState<string>("")
  const [productInfo, setProductInfo] = useState<App.ProductInfo>(initialProductInfo)
  const [federationFields, setFederationFields] = useState<App.Integrations[]>([])
  const [externalFields, setExternalFields] = useState<App.ExternalProductInfo>(initialExternalProductInfo)
  const [files, setFiles] = useState<App.ProductFilesObj>(initialProductFilesObj)
  const [productPrefixUrl, setProductPrefixUrl] = useState(isTypeUpdate ? prefixUrl : "")

  const handleModalState = useCallback(() => {
    appDispatch(clearProduct())
    appDispatch(changeModalState(false, ""))
    setError("")
  }, [])

  const mutationFn = modal.type === "create" ? apiProducts.createProductWithFiles : apiProducts.updateProductWithFiles

  const productMutation = useMutation({
    mutationFn: async (productData: App.Product) => {
      await mutationFn(productData, files, modal.type === "update" ? id : "")
    },
    onSuccess: () => {
      window.location.reload()
    },
    onError: () => {
      setError("Verifique os dados fornecidos.")
    }
  })

  const handleSubmit = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      const formData = new FormData(e.currentTarget)

      const productData: App.Product = {
        ...product,
        name: formData.get("name") as string,
        prefixUrl: formData.get("prefixUrl") as string,
        isFederated,
        isExternal,
        federatedKey,
        federationFields,
        productInfo: {
          ...productInfo,
          ...externalFields
        }
      }

      productMutation.mutate(productData)
    },
    [isFederated, isExternal, federatedKey, federationFields, productInfo, externalFields]
  )

  const handleIntegrationListChange = useCallback(
    (newFederationFields: App.Integrations[]) => {
      setFederationFields(newFederationFields)
    },
    [setFederationFields]
  )

  const clearState = () => {
    setProductName("")
    setIsFederated(false)
    setIsExternal(false)
    setFederatedKey("")
    setProductInfo(initialProductInfo)
    setFederationFields([])
    setExternalFields(initialExternalProductInfo)
    setProductPrefixUrl("")
    setEmailError("")
    setDescriptionError("")
  }

  useEffect(() => {
    if (isTypeUpdate) {
      setProductName(product.name || "")
      setIsFederated(product.isFederated || false)
      setIsExternal(product.isExternal || false)
      setFederatedKey(product.federatedKey || "")
      setProductInfo(product.productInfo || initialProductInfo)
      setFederationFields(product.federationFields || [])
      setExternalFields({
        authMethod: product.productInfo.authMethod || "",
        authKey: product.productInfo.authKey || "",
        docLink: product.productInfo.docLink || "",
        externalApiAuthUrl: product.productInfo.externalApiAuthUrl || "",
        externalApiUrl: product.productInfo.externalApiUrl || "",
        docTitle: product.productInfo.docTitle || "",
        docDescription: product.productInfo.docDescription || ""
      })
      setProductPrefixUrl(product.prefixUrl || "")
    } else if (modal.type === "create") {
      clearState()
    }
  }, [modal, product])

  return (
    <ModalComponent openModal={modal.state && (isTypeCreate || isTypeUpdate)} closeModal={handleModalState} size="2-lg">
      <Container>
        <Heading variant="h4" color="primary-dark">
          {isTypeCreate ? "Criar Produto" : "Editar Produto"}
        </Heading>
        <HeaderContainer>
          <Heading variant="h6">
            {isTypeCreate
              ? "Para criar um produto, preencha os campos abaixo e ative as opções que desejar, fornecendo as informações necessárias."
              : "Para editar um produto, edite os campos e ative as opções que desejar, fornecendo as informações necessárias."}
          </Heading>
          <BodyText weight="semi-bold" size="small">
            Preencha os campos:
          </BodyText>
        </HeaderContainer>
        <form onSubmit={handleSubmit}>
          <BodyContainer>
            <ProductForm
              productInfo={productInfo}
              setProductInfo={setProductInfo}
              productName={productName}
              setProductName={setProductName}
              prefixUrl={prefixUrl}
              productPrefixUrl={productPrefixUrl}
              setProductPrefixUrl={setProductPrefixUrl}
              setFiles={setFiles}
              logoFile={files.logoUrl}
              emailError={emailError}
              setEmailError={setEmailError}
              descriptionError={descriptionError}
              setDescriptionError={setDescriptionError}
              image={files.image}
            />

            <IntegrationForm
              integrationList={federationFields}
              setIntegrationList={setFederationFields}
              onIntegrationListChange={handleIntegrationListChange}
              modalType={modal.type}
            />

            <SeparatorLine />

            <BodyText weight="semi-bold" size="small">
              Ative as opções que desejar, fornecendo as informações necessárias para cada campo:
            </BodyText>
            <IsExternal
              externalFields={externalFields}
              setExternalFields={setExternalFields}
              isExternal={isExternal}
              setIsExternal={setIsExternal}
            />
            <IsFederated
              isFederated={isFederated}
              setIsFederated={setIsFederated}
              federatedKey={federatedKey}
              setFederatedKey={setFederatedKey}
              productInfo={productInfo}
              setProductInfo={setProductInfo}
            />
            <ButtonsContainer>
              <ButtonCustom type="button" onClick={handleModalState} label="Cancelar" className={clsx("secondary")} />
              <ButtonCustom
                type="submit"
                label="Confirmar"
                className={clsx("background-gradient")}
                disabled={!!emailError || !!descriptionError}
              />
            </ButtonsContainer>
            <ErrorMsg>{error}</ErrorMsg>
          </BodyContainer>
        </form>
      </Container>
    </ModalComponent>
  )
}

export default memo(ProductModal)
